import React, { useMemo } from "react";
import {
  ArrowRight,
  Coins,
  Crown,
  Info,
  Layers,
  Pencil,
  Plus,
  Settings,
  TrendingUp,
  Ticket,
  Users,
} from "lucide-react";

// Promotions overview — single-page dashboard. Mirrors the reference screenshot:
// KPI strip + Coupons-at-a-glance + Schemes-at-a-glance + Membership tiers +
// Conversion summary, all on one scroll. CTAs ("Loyalty settings", "+ New
// coupon") and per-card "View all" buttons hand off to the parent's tab nav.

const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtPts = (n) => {
  const v = Number(n || 0);
  if (v >= 100000) return `${(v / 100000).toFixed(1)} L`;
  return fmtNum(v);
};

const STATUS_TONE = {
  active:    { bg: "#E6F6EC", fg: "#15803C", label: "Active"    },
  scheduled: { bg: "#E8F1FF", fg: "#1E63D6", label: "Scheduled" },
  paused:    { bg: "#FFF3D6", fg: "#9A6700", label: "Paused"    },
  expired:   { bg: "#F1F5F9", fg: "#64748B", label: "Expired"   },
  draft:     { bg: "#F1F5F9", fg: "#64748B", label: "Draft"     },
  archived:  { bg: "#F1F5F9", fg: "#64748B", label: "Archived"  },
  inactive:  { bg: "#F1F5F9", fg: "#64748B", label: "Inactive"  },
};

const StatusBadge = ({ status }) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.draft;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: tone.bg, color: tone.fg }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.fg }} />
      {tone.label}
    </span>
  );
};

const TypePill = ({ type, label, tone }) => {
  const tones = {
    pct:   { bg: "#E0E7FF", fg: "#4338CA" },
    flat:  { bg: "#E6F6EC", fg: "#15803C" },
    bogo:  { bg: "#F0E9FF", fg: "#7C3AED" },
    tier:  { bg: "#FFF3D6", fg: "#9A6700" },
    blue:  { bg: "#E8F1FF", fg: "#1E63D6" },
    green: { bg: "#E6F6EC", fg: "#15803C" },
    gold:  { bg: "#FBF0DC", fg: "#B7791F" },
    teal:  { bg: "#DCFCE7", fg: "#047857" },
    purple:{ bg: "#F0E9FF", fg: "#7C3AED" },
    amber: { bg: "#FFF3D6", fg: "#9A6700" },
  };
  const t = tones[tone || type] || { bg: "#F1F5F9", fg: "#475569" };
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold"
      style={{ background: t.bg, color: t.fg }}
    >
      {label}
    </span>
  );
};

const couponTypeMeta = (c) => {
  const dt = c.discount_type;
  if (dt === "percent") {
    const pct = c.config?.percent ?? c.config?.percentage ?? "";
    return { label: `% ${pct}% off`.trim(), tone: "pct" };
  }
  if (dt === "flat") {
    const amt = c.config?.flat_amount ?? c.config?.flat ?? "";
    return { label: `₹ ₹${amt} off`, tone: "flat" };
  }
  if (dt === "bogo") return { label: "BOGO", tone: "bogo" };
  if (dt === "tiered") return { label: "Tiered", tone: "tier" };
  return { label: dt || "—", tone: "pct" };
};

const schemeTypeMeta = (s) => {
  const t = s.type;
  if (t === "disc_bill_pct")  return { label: "Bill %",          tone: "green" };
  if (t === "disc_bill_amt")  return { label: "Bill Flat ₹",     tone: "teal" };
  if (t === "free_slab")      return { label: "Free Slab",       tone: "gold" };
  if (t === "disc_material")  return { label: "Material Disc.",  tone: "blue" };
  if (t === "combo")          return { label: "Combo",           tone: "purple" };
  if (t === "employee")       return { label: "Employee",        tone: "amber" };
  return { label: t || "—", tone: "blue" };
};

const KpiCard = ({ icon, tone, label, value, hint, hintTone }) => {
  const toneBg = { gold: "#FBF0DC", green: "#E6F6EC", purple: "#F0E9FF", blue: "#E8F1FF" }[tone] || "#F1F5F9";
  const toneFg = { gold: "#B7791F", green: "#15803C", purple: "#7C3AED", blue: "#1E63D6" }[tone] || "#475569";
  return (
    <div className="bg-white border border-[#E6E9EF] rounded-xl px-4 py-3.5">
      <div className="flex items-center gap-2 mb-2">
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ background: toneBg, color: toneFg }}
        >
          {icon}
        </div>
        <div className="text-[12px] text-[#64748B] font-medium">{label}</div>
      </div>
      <div className="text-[26px] font-extrabold text-[#0F172A] leading-tight tabular-nums">
        {value}
      </div>
      {hint && (
        <div className="text-[11px] font-semibold mt-0.5" style={{ color: hintTone || "#15803C" }}>
          {hint}
        </div>
      )}
    </div>
  );
};

const CardShell = ({ icon, tone, title, subtitle, action, children }) => {
  const toneBg = { gold: "#FBF0DC", green: "#E6F6EC", purple: "#F0E9FF", blue: "#E8F1FF" }[tone] || "#F1F5F9";
  const toneFg = { gold: "#B7791F", green: "#15803C", purple: "#7C3AED", blue: "#1E63D6" }[tone] || "#475569";
  return (
    <div className="bg-white border border-[#E6E9EF] rounded-xl overflow-hidden">
      <div className="flex items-start justify-between gap-3 px-4 py-3 border-b border-[#EEF1F5]">
        <div className="flex items-center gap-2.5">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ background: toneBg, color: toneFg }}
          >
            {icon}
          </div>
          <div>
            <div className="text-[14px] font-bold text-[#0F172A]">{title}</div>
            {subtitle && <div className="text-[12px] text-[#64748B] mt-0.5">{subtitle}</div>}
          </div>
        </div>
        {action}
      </div>
      {children}
    </div>
  );
};

const ViewAllBtn = ({ onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] font-semibold text-[#475569] bg-white border border-[#E6E9EF] rounded-md hover:bg-[#F8FAFC]"
  >
    View all <ArrowRight size={12} />
  </button>
);

const PromotionsOverview = ({
  loyaltyConfig,
  coupons = [],
  schemes = [],
  readOnly,
  goTo,
  onNewCoupon,
}) => {
  // ── Loyalty derived ───────────────────────────────────────────────────
  const cfg = loyaltyConfig || {};
  const tiers = Array.isArray(cfg.tiers) ? cfg.tiers : [];
  const earnRate = cfg.earn_config?.rate;
  const earnPer = cfg.earn_config?.per;
  const pointValue = cfg.redeem_config?.point_value;
  const minRedeem = cfg.redeem_config?.min_redemption_pts;
  const maxPerBillPct = cfg.redeem_config?.max_per_bill_percent;

  // Memberships / outstanding — sourced only if the loyalty config returns
  // them; otherwise dash. Avoid synthesizing numbers.
  const membersTotal = cfg.stats?.members_total;
  const pointsOutstanding = cfg.stats?.points_outstanding;
  const ptsLiability = cfg.stats?.points_liability;

  // ── Coupon / scheme KPI counts ────────────────────────────────────────
  const activeCoupons    = coupons.filter((c) => c.status === "active").length;
  const totalCoupons     = coupons.length;
  const activeSchemes    = schemes.filter((s) => s.status === "active").length;
  const totalSchemes     = schemes.length;

  // ── Top-5 preview rows ────────────────────────────────────────────────
  const topCoupons = useMemo(() => {
    const sorted = [...coupons].sort((a, b) => {
      const order = { active: 0, scheduled: 1, paused: 2, draft: 3, expired: 4, archived: 5 };
      const oa = order[a.status] ?? 9;
      const ob = order[b.status] ?? 9;
      if (oa !== ob) return oa - ob;
      return (b.updated_at || 0).localeCompare(a.updated_at || 0);
    });
    return sorted.slice(0, 5);
  }, [coupons]);

  const topSchemes = useMemo(() => {
    const sorted = [...schemes].sort((a, b) => {
      const order = { active: 0, scheduled: 1, paused: 2, draft: 3, inactive: 4, archived: 5 };
      const oa = order[a.status] ?? 9;
      const ob = order[b.status] ?? 9;
      if (oa !== ob) return oa - ob;
      return (b.priority || 0) - (a.priority || 0);
    });
    return sorted.slice(0, 5);
  }, [schemes]);

  return (
    <div className="px-6 py-5 max-w-[1400px] space-y-4">
      {/* ── Header ───────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-[18px] font-bold text-[#0F172A]">
            Loyalty &amp; Offers Overview
          </h2>
          <p className="text-[13px] text-[#64748B] mt-1 max-w-[680px]">
            Configure how customers earn and redeem points, and manage every
            coupon and discount applied at billing.
          </p>
        </div>
        {!readOnly && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => goTo?.("earn")}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-semibold text-[#0F172A] bg-white border border-[#E6E9EF] rounded-md hover:bg-[#F8FAFC]"
            >
              <Settings size={14} /> Loyalty settings
            </button>
            <button
              type="button"
              onClick={onNewCoupon}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-bold text-white bg-primary rounded-md hover:opacity-90"
            >
              <Plus size={14} /> New coupon
            </button>
          </div>
        )}
      </div>

      {/* ── KPI strip ────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KpiCard
          icon={<Users size={16} />} tone="blue"
          label="Loyalty members"
          value={membersTotal != null ? fmtNum(membersTotal) : "—"}
        />
        <KpiCard
          icon={<Coins size={16} />} tone="gold"
          label="Points outstanding"
          value={pointsOutstanding != null ? fmtPts(pointsOutstanding) : "—"}
          hint={ptsLiability != null ? `≈ ₹${fmtPts(ptsLiability)} liability` : null}
          hintTone="#64748B"
        />
        <KpiCard
          icon={<Ticket size={16} />} tone="purple"
          label="Active coupons"
          value={fmtNum(activeCoupons)}
          hint={`${fmtNum(totalCoupons)} total`}
          hintTone="#64748B"
        />
        <KpiCard
          icon={<Layers size={16} />} tone="green"
          label="Active schemes"
          value={fmtNum(activeSchemes)}
          hint={`${fmtNum(totalSchemes)} total`}
          hintTone="#64748B"
        />
      </div>

      {/* ── 2-col body ───────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left column — Coupons + Schemes glance */}
        <div className="lg:col-span-2 space-y-4">
          <CardShell
            icon={<Ticket size={16} />} tone="purple"
            title="Coupons"
            subtitle="Discount codes available at billing"
            action={<ViewAllBtn onClick={() => goTo?.("coupons")} />}
          >
            {topCoupons.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-[#64748B]">
                No coupons yet. Click <span className="font-semibold">+ New coupon</span> to add one.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[13px]">
                  <thead>
                    <tr className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider bg-[#F8FAFC]">
                      <th className="px-4 py-2.5 text-left">Code</th>
                      <th className="px-4 py-2.5 text-left">Type</th>
                      <th className="px-4 py-2.5 text-left">Status</th>
                      <th className="px-4 py-2.5 text-right">Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topCoupons.map((c) => {
                      const meta = couponTypeMeta(c);
                      const code = c.config?.single_code || c.config?.series?.prefix || "—";
                      const used = Number(c.usage_count ?? c.used_count ?? 0);
                      const cap = Number(
                        c.config?.max_total_redemptions ??
                        c.config?.series?.generation_config?.generation_quantity ??
                        0
                      );
                      return (
                        <tr key={c.id} className="border-t border-[#EEF1F5] hover:bg-[#FAFBFC]">
                          <td className="px-4 py-3">
                            <div className="font-mono font-semibold text-[#0F172A]">{code}</div>
                            <div className="text-[12px] text-[#64748B] mt-0.5">{c.name}</div>
                          </td>
                          <td className="px-4 py-3">
                            <TypePill label={meta.label} tone={meta.tone} />
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                          <td className="px-4 py-3 text-right tabular-nums text-[#0F172A]">
                            <span className="font-semibold">{fmtNum(used)}</span>
                            <span className="text-[#94A3B8]"> / {cap ? fmtNum(cap) : "∞"}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardShell>

          <CardShell
            icon={<Layers size={16} />} tone="green"
            title="Schemes"
            subtitle="Bill-level, free-slab, combo and employee benefits"
            action={<ViewAllBtn onClick={() => goTo?.("schemes")} />}
          >
            {topSchemes.length === 0 ? (
              <div className="px-4 py-8 text-center text-[13px] text-[#64748B]">
                No schemes yet. Open the Schemes tab to create one.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-[13px]">
                  <thead>
                    <tr className="text-[11px] font-bold text-[#64748B] uppercase tracking-wider bg-[#F8FAFC]">
                      <th className="px-4 py-2.5 text-left">Name</th>
                      <th className="px-4 py-2.5 text-left">Type</th>
                      <th className="px-4 py-2.5 text-left">Status</th>
                      <th className="px-4 py-2.5 text-right">Priority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {topSchemes.map((s) => {
                      const meta = schemeTypeMeta(s);
                      return (
                        <tr key={s.id} className="border-t border-[#EEF1F5] hover:bg-[#FAFBFC]">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-[#0F172A]">{s.name}</div>
                            {s.description && (
                              <div className="text-[12px] text-[#64748B] mt-0.5 line-clamp-1">{s.description}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <TypePill label={meta.label} tone={meta.tone} />
                          </td>
                          <td className="px-4 py-3"><StatusBadge status={s.status} /></td>
                          <td className="px-4 py-3 text-right tabular-nums font-semibold text-[#0F172A]">
                            {s.priority ?? "—"}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardShell>
        </div>

        {/* Right column — Membership tiers + Conversion */}
        <div className="space-y-4">
          <CardShell
            icon={<Crown size={16} />} tone="gold"
            title="Membership tiers"
            action={
              !readOnly && (
                <button
                  type="button"
                  onClick={() => goTo?.("tiers")}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-[12px] font-semibold text-[#475569] bg-white border border-[#E6E9EF] rounded-md hover:bg-[#F8FAFC]"
                >
                  <Pencil size={12} /> Edit
                </button>
              )
            }
          >
            <div className="p-4 space-y-3">
              {tiers.length === 0 ? (
                <div className="text-[13px] text-[#64748B]">No tiers configured yet.</div>
              ) : (
                tiers.map((t) => (
                  <div key={t.id || t.name} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span
                        className="w-3 h-3 rounded-full"
                        style={{ background: t.color || "#94A3B8" }}
                      />
                      <span className="text-[14px] font-semibold text-[#0F172A]">{t.name}</span>
                    </div>
                    <span className="text-[13px] font-semibold text-[#475569]">
                      {t.multiplier ? `${t.multiplier}× pts` : "1× pts"}
                    </span>
                  </div>
                ))
              )}
            </div>
          </CardShell>

          <CardShell
            icon={<TrendingUp size={16} />} tone="green"
            title="Conversion at a glance"
          >
            <div className="p-4 space-y-2.5">
              <Row label="Earn rate"
                value={earnRate != null && earnPer != null ? `${earnRate} pt / ₹${earnPer}` : "—"} />
              <Row label="Point value"
                value={pointValue != null ? `₹${pointValue} / pt` : "—"} />
              <Row label="Min. redemption"
                value={minRedeem != null ? `${fmtNum(minRedeem)} pts` : "—"} />
              <Row label="Max per bill"
                value={maxPerBillPct != null ? `${maxPerBillPct}% of total` : "—"} />
              {!readOnly && (
                <button
                  type="button"
                  onClick={() => goTo?.("redeem")}
                  className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[13px] font-semibold text-primary bg-[#F8FAFF] border border-[#E0E7FF] rounded-md hover:bg-[#EEF4FF]"
                >
                  Adjust conversion <ArrowRight size={12} />
                </button>
              )}
            </div>
          </CardShell>
        </div>
      </div>

      {/* ── Note banner ──────────────────────────────────────────────── */}
      <div className="flex items-start gap-2 px-3.5 py-3 bg-[#F0F6FF] border border-[#DBEAFE] rounded-md text-[12.5px] text-[#1E40AF]">
        <Info size={14} className="mt-0.5 shrink-0" />
        <span>
          Changes to earning, redemption and coupon rules apply to <b>new
          transactions</b> only. Bills already settled are not recalculated.
        </span>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-[13px] text-[#64748B]">{label}</span>
    <span className="text-[13px] font-semibold text-[#0F172A] tabular-nums">{value}</span>
  </div>
);

export default PromotionsOverview;
